import { Injectable, OnInit } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { environment } from '@env/environment';
import { filter } from 'rxjs/operators';

@Injectable({
	providedIn: 'root'
})
export class SubfolderService implements OnInit {
	private _basePath: string = '';
	private _cleanBasePath: string = '';
	private _appRoutes: string[] = [];

	constructor(private readonly router: Router) {
		// Initialize registered routes
		this._appRoutes = this.getAllRegisteredRoutes();

		// Detect base path immediately on service creation
		this.updateBasePath();

		// Listen for router navigation events to refine detection if needed
		this.router.events.pipe(
			filter(event => event instanceof NavigationEnd)
		).subscribe(() => {
			// Refine base path on each navigation
			this.updateBasePath();
		});
	}

	ngOnInit() {
		// Implementation for OnInit interface
	}

	/**
	 * Updates both the full base path and clean base path
	 */
	private updateBasePath(): void {
		this._basePath = this.detectBasePath();
		this._cleanBasePath = this.cleanPath(this._basePath);
	}

	/**
	 * Convert path with slashes to clean format
	 * - "/Demo/" becomes "Demo"
	 * - "/" becomes ""
	 */
	private cleanPath(path: string): string {
		// Remove leading and trailing slashes
		let clean = path.replaceAll(/^\/+|\/+$/g, '');

		// If it's just the root path, return empty string
		return clean === '' ? '' : clean;
	}

	/**
	 * Get the current base path (subfolder) the application is running in
	 * with slashes (e.g., "/Demo/")
	 */
	get basePath(): string {
		return this._basePath;
	}

	/**
	 * Get the clean subfolder name without slashes (e.g., "Demo")
	 * or empty string for root
	 */
	get subfolder(): string {
		return this._cleanBasePath;
	}

	/**
	 * Detects the base path (subfolder) the application is running in
	 * while excluding Angular routes
	 */
	private detectBasePath(): string {
		// Skip dynamic detection in development mode
		if (!environment.production) {
			return '/';
		}

		// Get the current URL path from the window location
		const fullPath = globalThis.location.pathname;

		// Get the current Angular route
		const currentRoute = this.router.url;

		// Remove the Angular route from the full path to get the base path
		let basePath = fullPath;

		// If we have a current route and it's part of the path
		if (currentRoute && currentRoute !== '/' && fullPath.endsWith(currentRoute)) {
			basePath = fullPath.substring(0, fullPath.length - currentRoute.length);
		} else {
			// More complex approach: check each segment against known routes
			const pathSegments = fullPath.split('/').filter(segment => segment.length > 0);

			// Find the index of the first segment that is a known route
			let routeStartIndex = -1;
			for (let i = 0; i < pathSegments.length; i++) {
				if (this._appRoutes.includes(pathSegments[i])) {
					routeStartIndex = i;
					break;
				}
			}

			if (routeStartIndex >= 0) {
				// Only take segments before the first route segment
				basePath = '/' + pathSegments.slice(0, routeStartIndex).join('/');
			}
		}

		// Ensure the base path ends with a slash
		if (!basePath.endsWith('/')) {
			basePath += '/';
		}

		// If we end up with just '/', that means no subfolder detected
		return basePath;
	}

	/**
	 * Gets all registered Angular routes to exclude them from subfolder detection
	 */
	private getAllRegisteredRoutes(): string[] {
		const routes: string[] = [];

		// Recursive function to extract all route paths
		const extractRoutes = (routeConfig: any[], parentPath: string = '') => {
			routeConfig.forEach(route => {
				if (route.path) {
					// Get the first segment of the route path
					const segments = route.path.split('/');
					if (segments.length > 0 && segments[0]) {
						routes.push(segments[0]);
					}

					// Also add the full path
					const fullPath = parentPath ? `${parentPath}/${route.path}` : route.path;
					routes.push(fullPath);

					// Process child routes if any
					if (route.children) {
						extractRoutes(route.children, fullPath);
					}
				}
			});
		};

		// Extract all routes
		extractRoutes(this.router.config);

		// Add common Angular route patterns
		routes.push('reports');  // Explicitly add your known routes

		// Filter out duplicates and return
		return [...new Set(routes)];
	}

	/**
	 * Resolves a path relative to the detected base path
	 * @param path The path to resolve
	 */
	public resolvePath(path: string): string {
		// If in development, just return the path as-is
		if (!environment.production) {
			return path;
		}

		// Remove leading slash from input path if present
		const cleanPath = path.startsWith('/') ? path.substring(1) : path;

		// Combine base path with input path
		return this._basePath + cleanPath;
	}

	/**
	 * Manually set specific routes to exclude from subfolder detection
	 * @param routes Array of route names to exclude
	 */
	public setKnownRoutes(routes: string[]): void {
		this._appRoutes = [...this._appRoutes, ...routes];
		// Re-detect base path with new routes information
		this.updateBasePath();
	}
}